import { useEffect, useState, useCallback } from "react";
import { Gift } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-800",
    earned: "bg-green-100 text-green-800",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

export default function ReferralsPanel() {
  const [data, setData] = useState({ items: [], pending: 0, earned: 0, credit_per_referral: 25 });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/referrals");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="referrals-panel">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card-surface p-5">
          <Gift size={18} className="text-clay" />
          <div className="font-heading text-3xl mt-3">{data.pending + data.earned}</div>
          <div className="eyebrow mt-1">Total referrals</div>
        </div>
        <div className="card-surface p-5">
          <div className="font-heading text-3xl">{data.pending}</div>
          <div className="eyebrow mt-1">Pending payout</div>
        </div>
        <div className="card-surface p-5">
          <div className="font-heading text-3xl">${(data.earned || 0) * (data.credit_per_referral || 25)}</div>
          <div className="eyebrow mt-1">Credit earned</div>
        </div>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-xl mb-4">{err}</div>}

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone/60">
            <tr className="text-left">
              <th className="px-5 py-3 eyebrow">When</th>
              <th className="px-5 py-3 eyebrow">Referrer</th>
              <th className="px-5 py-3 eyebrow">Referee</th>
              <th className="px-5 py-3 eyebrow">Code</th>
              <th className="px-5 py-3 eyebrow">Credit</th>
              <th className="px-5 py-3 eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan="6" className="px-5 py-10 text-center text-muted2">No referrals yet.</td></tr>
            )}
            {data.items.map((r) => (
              <tr key={r.id} className="border-t border-black/5" data-testid={`referral-row-${r.id}`}>
                <td className="px-5 py-4 text-xs text-muted2">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-4 text-xs">{r.referrer_email}</td>
                <td className="px-5 py-4 text-xs">{r.referee_email}</td>
                <td className="px-5 py-4 font-heading tracking-widest">{r.referrer_code}</td>
                <td className="px-5 py-4 font-heading">${r.credit_amount}</td>
                <td className="px-5 py-4"><StatusBadge status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
